import { Fragment, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    ComposedChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from "recharts";
import {
    Gauge,
    Trophy,
    TrendingUp,
    PieChart as PieIcon,
    AlertTriangle,
    TrendingDown,
    Receipt,
    CalendarDays,
} from "lucide-react";
import { OPS } from "@/constants/testIds";
import {
    fmtNumber,
    fmtByUnit,
    fmtCompact,
    currencySuffix,
    CHART_COLORS,
    FIN_COLORS,
    PROFIT_COLOR,
    LOSS_COLOR,
} from "@/lib/format";
import { groupLabel, accountName, finCurrency } from "@/lib/finLabels";

const META = {
    kpi: { name_ar: "المؤشرات المالية", name_en: "Financial KPIs", icon: Gauge },
    ranking: { name_ar: "الربح حسب الفرع", name_en: "Profit by Branch", icon: Trophy },
    monthly: { name_ar: "الربحية الشهرية", name_en: "Monthly Profitability", icon: TrendingUp },
    expenses: { name_ar: "تركيب المصروفات", name_en: "Expense Breakdown", icon: PieIcon },
    detail: { name_ar: "التفاصيل الشهرية", name_en: "Monthly Detail", icon: Receipt },
};

const AXIS_TICK = { fontSize: 11, fill: "var(--ink-muted)" };
const GRID_COLOR = "var(--border-soft)";

// ---------------------------------------------------------------- data hook
function useFinData(path, { branch, start, end } = {}, sendBranch = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const bKey = sendBranch ? branch || "all" : "";
    useEffect(() => {
        let active = true;
        setLoading(true);
        const params = {};
        if (sendBranch) params.branch = branch || "all";
        if (start) params.start = start;
        if (end) params.end = end;
        api
            .get(path, { params })
            .then((r) => active && setData(r.data))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, bKey, start, end, sendBranch]);
    return { data, loading };
}

// ---------------------------------------------------------------- shells
function SectionCard({ testId, metaKey, children, badge }) {
    const { tn } = useI18n();
    const meta = META[metaKey];
    const Icon = meta.icon;
    return (
        <Card data-testid={testId} className="surface border-[color:var(--border-soft)] rounded-xl shadow-[var(--shadow-sm)]">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-bold text-[color:var(--ink)]">
                    <Icon className="h-[18px] w-[18px] text-[color:var(--brand-brown)]" />
                    {tn(meta)}
                </CardTitle>
                {badge}
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

function PartialBadge({ months }) {
    const { t } = useI18n();
    if (!months || !months.length) return null;
    return (
        <Badge
            data-testid={OPS.financials.partialBadge}
            variant="secondary"
            className="bg-[color:rgba(209,128,47,0.14)] text-[color:var(--brand-brown)] border-0 gap-1"
        >
            <AlertTriangle className="h-3.5 w-3.5" />
            {t("partial_months_label")}: <bdi dir="ltr">{months.join("، ")}</bdi>
        </Badge>
    );
}

// ---------------------------------------------------------------- KPI
export function FinKpiSection({ branch, start, end }) {
    const { data, loading } = useFinData("/financials/kpi", { branch, start, end });
    const { lang, t } = useI18n();
    const cards = data?.cards || [];

    const valueClass = (c) => {
        if (c.key === "net" || c.key === "margin") {
            if (c.value === null || c.value === undefined) return "text-[color:var(--ink)]";
            return c.value < 0 ? "text-[color:var(--danger)]" : "text-[color:var(--success)]";
        }
        return "text-[color:var(--ink)]";
    };

    return (
        <SectionCard testId={OPS.financials.kpi} metaKey="kpi" badge={!loading ? <PartialBadge months={data?.partial_months} /> : null}>
            {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-[92px] rounded-xl" />
                    ))}
                </div>
            ) : !data?.data_available ? (
                <EmptyState compact title={t("financials_empty")} dataset="financials_pl_monthly" />
            ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {cards.map((c) => (
                        <div
                            key={c.key}
                            data-testid={OPS.financials.kpiCard(c.key)}
                            className="rounded-xl surface-2 border border-[color:var(--border-soft)] p-4"
                        >
                            <p className="text-xs text-[color:var(--ink-muted)] truncate flex items-center gap-1">
                                {lang === "ar" ? c.name_ar : c.name_en}
                                {c.key === "net" && c.value < 0 ? (
                                    <TrendingDown className="h-3.5 w-3.5 text-[color:var(--danger)]" />
                                ) : null}
                            </p>
                            <p className={`mt-2 text-2xl font-extrabold tnum ${valueClass(c)}`}>
                                {fmtByUnit(c.value, c.unit, lang)}
                                {c.unit === "currency" && c.value !== null ? (
                                    <span className="ms-1 text-xs font-medium text-[color:var(--ink-muted)]">
                                        {currencySuffix(lang)}
                                    </span>
                                ) : null}
                            </p>
                            {c.key === "net" && c.value < 0 ? (
                                <p className="mt-1 text-[11px] font-semibold text-[color:var(--danger)]">{t("loss_label")}</p>
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
        </SectionCard>
    );
}

// ---------------------------------------------------------------- Branch profit ranking
export function FinBranchRankingSection({ start, end }) {
    const { data, loading } = useFinData("/financials/branches", { start, end }, false);
    const { tn, t, lang } = useI18n();
    const rows = data?.rows || [];
    const chartData = rows.map((r) => ({ name: tn(r.branch), net: r.net, is_loss: r.is_loss }));
    const chartHeight = Math.max(260, rows.length * 34 + 40);

    return (
        <SectionCard
            testId={OPS.financials.ranking}
            metaKey="ranking"
            badge={!loading && rows.length ? (
                <Badge variant="secondary" className="bg-[color:rgba(186,217,235,0.6)] text-[color:var(--brand-brown)] border-0">
                    {rows.length} {lang === "ar" ? "فرع" : "branches"}
                </Badge>
            ) : null}
        >
            {loading ? (
                <Skeleton className="h-[320px] rounded-xl" />
            ) : !rows.length ? (
                <EmptyState icon={Trophy} title={t("financials_empty")} dataset="financials_pl_monthly" />
            ) : (
                <div className="space-y-5">
                    {/* Horizontal bar chart */}
                    <div dir="ltr" data-testid={OPS.financials.rankingChart}>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                                <XAxis type="number" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "var(--ink)" }} tickLine={false} axisLine={false} />
                                <ReferenceLine x={0} stroke="var(--border-strong)" />
                                <Tooltip
                                    cursor={{ fill: "rgba(186,217,235,0.18)" }}
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        const d = payload[0].payload;
                                        return (
                                            <div dir={lang === "ar" ? "rtl" : "ltr"} className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs">
                                                <p className="font-semibold text-[color:var(--ink)]">{d.name}</p>
                                                <p className={`tnum ${d.net < 0 ? "text-[color:var(--danger)]" : "text-[color:var(--success)]"}`}>
                                                    {fmtNumber(d.net, lang, 2)} {currencySuffix(lang)}
                                                </p>
                                            </div>
                                        );
                                    }}
                                />
                                <Bar dataKey="net" radius={[0, 4, 4, 0]} maxBarSize={22}>
                                    {chartData.map((d, i) => (
                                        <Cell key={i} fill={d.is_loss ? LOSS_COLOR : PROFIT_COLOR} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto rounded-lg border border-[color:var(--border-soft)]">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-[color:rgba(224,222,217,0.55)] hover:bg-[color:rgba(224,222,217,0.55)]">
                                    <TableHead className="text-start">{t("branch")}</TableHead>
                                    <TableHead className="text-end whitespace-nowrap">{t("revenue_col")}</TableHead>
                                    <TableHead className="text-end whitespace-nowrap">{t("expense_col")}</TableHead>
                                    <TableHead className="text-end whitespace-nowrap">{t("net_col")}</TableHead>
                                    <TableHead className="text-end whitespace-nowrap">{t("margin_col")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow
                                        key={r.branch.id}
                                        data-testid={OPS.financials.rankRow(r.branch.id)}
                                        className={`hover:bg-[color:rgba(186,217,235,0.18)] ${r.is_loss ? "bg-[color:rgba(180,35,24,0.04)]" : ""}`}
                                    >
                                        <TableCell className="font-medium text-[color:var(--ink)] whitespace-nowrap">
                                            <span className="flex items-center gap-2">
                                                {tn(r.branch)}
                                                {r.partial ? (
                                                    <Badge variant="secondary" className="bg-[color:rgba(209,128,47,0.14)] text-[color:var(--brand-brown)] border-0 text-[10px] px-1.5 py-0">
                                                        {t("partial_short")}
                                                    </Badge>
                                                ) : null}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-end tnum text-[color:var(--ink)]">{fmtNumber(r.revenue, lang, 2)}</TableCell>
                                        <TableCell className="text-end tnum text-[color:var(--ink)]">{fmtNumber(r.expense, lang, 2)}</TableCell>
                                        <TableCell className={`text-end tnum font-semibold ${r.is_loss ? "text-[color:var(--danger)]" : "text-[color:var(--success)]"}`}>
                                            {fmtNumber(r.net, lang, 2)}
                                        </TableCell>
                                        <TableCell className={`text-end tnum ${r.is_loss ? "text-[color:var(--danger)]" : "text-[color:var(--ink)]"}`}>
                                            {r.margin === null ? "—" : `${fmtNumber(r.margin, lang, 1)}%`}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}
        </SectionCard>
    );
}

// ---------------------------------------------------------------- Monthly profitability
export function FinMonthlySection({ branch, start, end }) {
    const { data, loading } = useFinData("/financials/monthly", { branch, start, end });
    const { lang, t } = useI18n();
    const months = data?.months || [];
    const rows = months.map((m) => ({
        label: lang === "ar" ? m.label_ar : m.label_en,
        revenue: m.revenue,
        expense: m.expense,
        net: m.net,
        partial: m.partial,
    }));
    const partialLabels = new Set(rows.filter((r) => r.partial).map((r) => r.label));
    const partialMonthsLabels = months.filter((m) => m.partial).map((m) => (lang === "ar" ? m.label_ar : m.label_en));

    const nameRevenue = lang === "ar" ? "الإيرادات" : "Revenue";
    const nameExpense = lang === "ar" ? "المصروفات" : "Expenses";
    const nameNet = lang === "ar" ? "صافي الربح" : "Net Profit";

    const MonthTick = ({ x, y, payload }) => {
        const isP = partialLabels.has(payload.value);
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={12} textAnchor="middle" fill="var(--ink-muted)" fontSize={11}>
                    {payload.value}
                </text>
                {isP ? (
                    <text x={0} y={0} dy={26} textAnchor="middle" fill="hsl(28 62% 45%)" fontSize={9} fontWeight="600">
                        {t("partial_short")}
                    </text>
                ) : null}
            </g>
        );
    };

    return (
        <SectionCard testId={OPS.financials.monthly} metaKey="monthly" badge={!loading ? <PartialBadge months={partialMonthsLabels} /> : null}>
            {loading ? (
                <Skeleton className="h-[300px] rounded-xl" />
            ) : !rows.length ? (
                <EmptyState icon={TrendingUp} title={t("financials_empty")} dataset="financials_pl_monthly" />
            ) : (
                <div dir="ltr" data-testid={OPS.financials.monthlyChart}>
                    <ResponsiveContainer width="100%" height={320}>
                        <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 18 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                            <XAxis dataKey="label" tick={<MonthTick />} interval={0} tickLine={false} axisLine={{ stroke: GRID_COLOR }} height={40} />
                            <YAxis tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
                            <ReferenceLine y={0} stroke="var(--border-strong)" />
                            <Tooltip
                                cursor={{ fill: "rgba(186,217,235,0.18)" }}
                                content={({ active, payload, label }) => {
                                    if (!active || !payload?.length) return null;
                                    const isP = partialLabels.has(label);
                                    return (
                                        <div dir={lang === "ar" ? "rtl" : "ltr"} className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs">
                                            <p className="font-semibold text-[color:var(--ink)] mb-1">
                                                {label}
                                                {isP ? <span className="ms-1 text-[color:var(--brand-brown)]">({t("partial_short")})</span> : null}
                                            </p>
                                            {payload.map((p) => (
                                                <p key={p.dataKey} className="flex items-center gap-2 text-[color:var(--ink-muted)]">
                                                    <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
                                                    {p.name}: <span className="tnum font-semibold text-[color:var(--ink)]">{fmtNumber(p.value, lang, 2)}</span>
                                                </p>
                                            ))}
                                        </div>
                                    );
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar yAxisId={0} dataKey="revenue" name={nameRevenue} fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} maxBarSize={36} />
                            <Bar yAxisId={0} dataKey="expense" name={nameExpense} fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} maxBarSize={36} />
                            <Line yAxisId={0} type="monotone" dataKey="net" name={nameNet} stroke="hsl(var(--chart-3))" strokeWidth={2.5} dot={{ r: 3 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
        </SectionCard>
    );
}

// ---------------------------------------------------------------- Expense breakdown
export function FinExpenseSection({ branch, start, end }) {
    const { data, loading } = useFinData("/financials/expenses", { branch, start, end });
    const { lang, t } = useI18n();
    const groups = data?.groups || [];
    const pieData = groups.map((g) => ({ name: groupLabel(g.key, lang), key: g.key, value: g.amount, share: g.share }));

    return (
        <SectionCard testId={OPS.financials.expenses} metaKey="expenses" badge={!loading ? <PartialBadge months={data?.partial_months} /> : null}>
            {loading ? (
                <Skeleton className="h-[260px] rounded-xl" />
            ) : !groups.length ? (
                <EmptyState icon={PieIcon} title={t("financials_empty")} dataset="financials_pl_monthly" />
            ) : (
                <div className="grid gap-4 md:grid-cols-2 items-center">
                    <div dir="ltr" data-testid={OPS.financials.expenseChart}>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={2} stroke="var(--bg-surface)" strokeWidth={2}>
                                    {pieData.map((entry, i) => (
                                        <Cell key={entry.key} fill={FIN_COLORS[i % FIN_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        const d = payload[0].payload;
                                        return (
                                            <div dir={lang === "ar" ? "rtl" : "ltr"} className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs">
                                                <p className="font-semibold text-[color:var(--ink)]">{d.name}</p>
                                                <p className="text-[color:var(--ink-muted)] tnum">{fmtNumber(d.value, lang, 2)} {currencySuffix(lang)}</p>
                                                <p className="text-[color:var(--ink-muted)] tnum">{fmtNumber(d.share, lang, 1)}%</p>
                                            </div>
                                        );
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        {data?.total != null ? (
                            <p className="text-center text-xs text-[color:var(--ink-muted)] -mt-2">
                                {t("expense_col")}: <span className="tnum font-semibold text-[color:var(--ink)]">{fmtNumber(data.total, lang, 2)} {currencySuffix(lang)}</span>
                            </p>
                        ) : null}
                    </div>
                    <div className="space-y-2">
                        {groups.map((g, i) => (
                            <div
                                key={g.key}
                                data-testid={OPS.financials.expenseRow(g.key)}
                                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${g.is_key ? "border-[color:var(--brand-orange)] bg-[color:rgba(209,128,47,0.06)]" : "border-[color:var(--border-soft)] surface-2"}`}
                            >
                                <span className="flex items-center gap-2 text-sm text-[color:var(--ink)] truncate">
                                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: FIN_COLORS[i % FIN_COLORS.length] }} />
                                    <span className={g.is_key ? "font-bold" : ""}>{groupLabel(g.key, lang)}</span>
                                    {g.is_key ? (
                                        <Badge variant="secondary" className="bg-[color:rgba(209,128,47,0.16)] text-[color:var(--brand-brown)] border-0 text-[10px] px-1.5 py-0">
                                            {t("key_cost")}
                                        </Badge>
                                    ) : null}
                                </span>
                                <span className="flex items-center gap-3 text-xs tnum">
                                    <span className="text-[color:var(--ink)] font-medium">{fmtNumber(g.amount, lang, 0)} {currencySuffix(lang)}</span>
                                    <span className="text-[color:var(--brand-brown)] font-semibold min-w-[44px] text-end">{fmtNumber(g.share, lang, 1)}%</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </SectionCard>
    );
}


// ---------------------------------------------------------------- data hook (monthly detail)
function useMonthlyDetail(month, branch) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const b = branch || "all";
    useEffect(() => {
        if (!month) {
            setData(null);
            setLoading(false);
            return;
        }
        let active = true;
        setLoading(true);
        api
            .get("/financials/monthly_detail", { params: { month, branch: b } })
            .then((r) => active && setData(r.data))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [month, b]);
    return { data, loading };
}

// ---------------------------------------------------------------- Monthly detail
// Full P&L line breakdown for one month + one branch (or All). Income lines
// listed flat, expense lines grouped by group with subtotals, net at the bottom.
export function FinMonthlyDetailSection({ branch, meta }) {
    const { lang, t, tn } = useI18n();
    const months = useMemo(() => meta?.months || [], [meta?.months]);
    const [month, setMonth] = useState(months.length ? months[months.length - 1].value : null);

    // Keep a valid month selected when coverage/meta changes.
    useEffect(() => {
        if (months.length && !months.some((m) => m.value === month)) {
            setMonth(months[months.length - 1].value);
        }
    }, [months, month]);

    const { data, loading } = useMonthlyDetail(month, branch);

    const monthLabel = (m) => (lang === "ar" ? m.label_ar : m.label_en);
    const money = (v) => `${fmtNumber(v, lang, 2)} ${finCurrency(lang)}`;

    const income = data?.income;
    const expense = data?.expense;
    const groups = expense?.groups || [];

    const branchCaption = data?.selected_branch ? tn(data.selected_branch) : t("all_branches_fin");
    const monthCaption = data ? (lang === "ar" ? data.month_label_ar : data.month_label_en) : "";

    const headerControls = (
        <div className="flex items-center gap-2">
            {data?.partial ? (
                <Badge
                    data-testid={OPS.financials.partialBadge}
                    variant="secondary"
                    className="bg-[color:rgba(209,128,47,0.14)] text-[color:var(--brand-brown)] border-0 gap-1"
                >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t("partial_short")}
                </Badge>
            ) : null}
            <Select value={month || ""} onValueChange={setMonth}>
                <SelectTrigger
                    data-testid={OPS.financials.monthlyDetailMonth}
                    className="w-[170px] surface gap-2"
                >
                    <CalendarDays className="h-4 w-4 text-[color:var(--brand-brown)]" />
                    <SelectValue placeholder={t("select_month")} />
                </SelectTrigger>
                <SelectContent>
                    {months.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                            {monthLabel(m)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );

    return (
        <SectionCard testId={OPS.financials.monthlyDetail} metaKey="detail" badge={headerControls}>
            {loading ? (
                <Skeleton className="h-[360px] rounded-xl" />
            ) : !data?.data_available ? (
                <EmptyState icon={Receipt} title={t("no_lines_month")} hint={`${branchCaption} · ${monthCaption}`} dataset="financials_pl_monthly" />
            ) : (
                <div className="space-y-3">
                    <p className="text-sm text-[color:var(--ink-muted)]">
                        <span className="font-semibold text-[color:var(--ink)]">{branchCaption}</span>
                        <span className="mx-1.5">·</span>
                        <bdi>{monthCaption}</bdi>
                    </p>

                    <div className="overflow-x-auto rounded-lg border border-[color:var(--border-soft)]">
                        <Table data-testid={OPS.financials.mdTable}>
                            <TableHeader>
                                <TableRow className="bg-[color:rgba(224,222,217,0.55)] hover:bg-[color:rgba(224,222,217,0.55)]">
                                    <TableHead className="text-start">{t("account_col")}</TableHead>
                                    <TableHead className="text-start whitespace-nowrap">{t("group_col")}</TableHead>
                                    <TableHead className="text-end whitespace-nowrap">{t("amount_col")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {/* INCOME */}
                                <TableRow className="bg-[color:rgba(120,160,90,0.12)] hover:bg-[color:rgba(120,160,90,0.12)]">
                                    <TableCell colSpan={3} className="font-bold text-[color:var(--ink)]">
                                        {lang === "ar" ? income?.name_ar : income?.name_en}
                                    </TableCell>
                                </TableRow>
                                {(income?.lines || []).map((ln) => (
                                    <TableRow
                                        key={`inc-${ln.account_id}`}
                                        data-testid={OPS.financials.mdLineRow(ln.account_id)}
                                        className="hover:bg-[color:rgba(186,217,235,0.18)]"
                                    >
                                        <TableCell className="text-[color:var(--ink)] ps-6">{accountName(ln.account_id, ln.account_name, lang)}</TableCell>
                                        <TableCell className="text-[color:var(--ink-muted)] text-xs whitespace-nowrap">
                                            {groupLabel(ln.group_key, lang)}
                                        </TableCell>
                                        <TableCell className="text-end tnum text-[color:var(--ink)]">{fmtNumber(ln.amount, lang, 2)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow className="border-t border-[color:var(--border-strong)] hover:bg-transparent">
                                    <TableCell colSpan={2} className="font-semibold text-[color:var(--ink)]">{t("income_total_label")}</TableCell>
                                    <TableCell className="text-end tnum font-bold text-[color:var(--success)]">{money(income?.subtotal)}</TableCell>
                                </TableRow>

                                {/* EXPENSE */}
                                <TableRow className="bg-[color:rgba(180,35,24,0.07)] hover:bg-[color:rgba(180,35,24,0.07)]">
                                    <TableCell colSpan={3} className="font-bold text-[color:var(--ink)]">
                                        {lang === "ar" ? expense?.name_ar : expense?.name_en}
                                    </TableCell>
                                </TableRow>
                                {groups.map((g) => (
                                    <Fragment key={`grp-${g.key}`}>
                                        <TableRow
                                            data-testid={OPS.financials.mdGroupRow(g.key)}
                                            className="bg-[color:rgba(224,222,217,0.4)] hover:bg-[color:rgba(224,222,217,0.4)]"
                                        >
                                            <TableCell colSpan={2} className="font-semibold text-[color:var(--ink)]">
                                                <span className="flex items-center gap-2">
                                                    {groupLabel(g.key, lang)}
                                                    {g.is_key ? (
                                                        <Badge variant="secondary" className="bg-[color:rgba(209,128,47,0.16)] text-[color:var(--brand-brown)] border-0 text-[10px] px-1.5 py-0">
                                                            {t("key_cost")}
                                                        </Badge>
                                                    ) : null}
                                                    {g.share != null ? (
                                                        <span className="text-[11px] font-normal text-[color:var(--ink-muted)] tnum">
                                                            {fmtNumber(g.share, lang, 1)}%
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-end tnum font-semibold text-[color:var(--ink)]">{fmtNumber(g.subtotal, lang, 2)}</TableCell>
                                        </TableRow>
                                        {g.lines.map((ln) => (
                                            <TableRow
                                                key={`exp-${g.key}-${ln.account_id}`}
                                                data-testid={OPS.financials.mdLineRow(ln.account_id)}
                                                className="hover:bg-[color:rgba(186,217,235,0.18)]"
                                            >
                                                <TableCell className="text-[color:var(--ink)] ps-8">{accountName(ln.account_id, ln.account_name, lang)}</TableCell>
                                                <TableCell className="text-[color:var(--ink-muted)] text-xs whitespace-nowrap">
                                                    {groupLabel(ln.group_key, lang)}
                                                </TableCell>
                                                <TableCell className="text-end tnum text-[color:var(--ink)]">{fmtNumber(ln.amount, lang, 2)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </Fragment>
                                ))}
                                <TableRow className="border-t border-[color:var(--border-strong)] hover:bg-transparent">
                                    <TableCell colSpan={2} className="font-semibold text-[color:var(--ink)]">{t("expense_total_label")}</TableCell>
                                    <TableCell className="text-end tnum font-bold text-[color:var(--ink)]">{money(expense?.subtotal)}</TableCell>
                                </TableRow>

                                {/* NET */}
                                <TableRow
                                    data-testid={OPS.financials.mdNetRow}
                                    className="border-t-2 border-[color:var(--border-strong)] bg-[color:rgba(224,222,217,0.45)] hover:bg-[color:rgba(224,222,217,0.45)]"
                                >
                                    <TableCell colSpan={2} className="text-base font-extrabold text-[color:var(--ink)]">
                                        <span className="flex items-center gap-2">
                                            {t("net_result_label")}
                                            {data?.is_loss ? <TrendingDown className="h-4 w-4 text-[color:var(--danger)]" /> : null}
                                        </span>
                                    </TableCell>
                                    <TableCell className={`text-end tnum text-base font-extrabold ${data?.is_loss ? "text-[color:var(--danger)]" : "text-[color:var(--success)]"}`}>
                                        {money(data?.net)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}
        </SectionCard>
    );
}
